import Sentry from "@similarweb/sentry-logger";
import type { BrowserOptions, ErrorEvent, EventHint } from "@similarweb/sentry-logger";

import { filterSentryErrorEvents } from "app/sentry/utils/sentryFilters";
import { ResponseError } from "app/services/fetchService";
import { router } from "single-spa/react-app/router/router";

const PRO_PREFIX_REG_EXP = /^([a-zA-Z0-9]+)-pro/;
// The maximum length of a tag value in Sentry
const MAX_TAG_VALUE_LENGTH = 200;

export const DENY_URLS = [/js\.qualified\.com\/qualified\.js/, /app\.qualified\.com/, /api\.chilipiper\.com/];

export const NETWORK_RESPONSE_HEADERS_WHITE_LIST = [
    "SW-Module-Disabled",
    "SW-Validation-error",
    "Error-Message",
    "Error-Code",
    "SW-AuthNeedRefresh",
    "SW-excel-credit",
    "SW-Allowed-Segments",
    "SW-Segments-Share",
    "SW-Excel-Charge_Error",
    "SW-segments-rules",
    "x-amz-cf-id",
];

const getUserSettings = () => {
    const {
        plan: packageName = undefined,
        accountId = undefined,
        username: userEmail = undefined,
        impersonateSource = undefined,
    } = window.similarweb?.settings?.user || {};
    const isImpersonation = !!impersonateSource;
    return { packageName, accountId, userEmail, isImpersonation };
};

// Check if the error is a custom error and add custom fields to the event because Sentry doesn't resolve them automatically
const updatedSentryEventWithCustomErrorFields = (event: ErrorEvent, hint: EventHint) => {
    const originalException = hint?.originalException;

    if (!event.exception) {
        return;
    }

    if (originalException instanceof ResponseError) {
        const responseErrorDetails = {
            response: originalException.response,
            status: originalException.status,
            formattedMessage: originalException.formattedMessage,
        };
        event.extra = {
            ...event.extra,
            responseErrorDetails,
        };
    }
};

export const createBaseConfig = (buildId: string): BrowserOptions => {
    const beforeSend = (event: ErrorEvent, hint: EventHint) => {
        if (!event.tags) {
            event.tags = {};
        }
        const transitionPhase = event.tags["transition.phase"];
        const isLoggedDuringTransition =
            transitionPhase && (transitionPhase === "onEnter" || transitionPhase === "redirectTo");
        if (isLoggedDuringTransition) {
            // Unset sticky route tags on events captured mid-transition, otherwise they are grouped by the previous unrelated page.
            delete event.tags["page.section"];
            delete event.tags["page.subSection"];
            delete event.tags["page.subSubSection"];
            delete event.tags.stateName;
            delete event.tags.hash;
        } else {
            const hash = window.location.hash.slice(1);
            if (hash) {
                event.tags.hash = hash.slice(0, MAX_TAG_VALUE_LENGTH);
            }
            const stateName = router?.globals?.current?.name;
            if (stateName) {
                event.tags.stateName = stateName;
            }
        }
        const sentryScope = Sentry.getCurrentScope();
        const sentryUser = sentryScope.getUser();
        // @ts-ignore FIXME: strict migration — getUser() may return undefined
        const isUserSet = Object.keys(sentryUser).length > 0;
        if (!isUserSet || (isUserSet && !sentryUser?.email)) {
            // Sentry are sometimes setting ip if user is not set, so we will check if it was already set
            const { packageName, accountId, userEmail, isImpersonation } = getUserSettings();
            if (userEmail) {
                // If at least one property was extracted from the user settings, the rest should be available as well
                Sentry.setUser({ email: userEmail });
                Sentry.setTag("package-name", packageName);
                Sentry.setTag("account-id", accountId);
                Sentry.setTag("user-email", userEmail);
                Sentry.setTag("isImpersonation", isImpersonation);
            }
        }

        const hostname = window.location.hostname;
        const proPrefixMatches = hostname.match(PRO_PREFIX_REG_EXP);

        const sandboxName = proPrefixMatches ? proPrefixMatches[1] : null;
        if (sandboxName) {
            event.tags.sandboxName = sandboxName;
        }

        updatedSentryEventWithCustomErrorFields(event, hint);

        return filterSentryErrorEvents(event, hint);
    };

    return {
        dsn: "https://25336446686d47cb9ba812b6891851d5@o289771.ingest.sentry.io/1527986",
        release: `pro-frontend@${buildId}`,
        beforeSend,
        ignoreErrors: [
            "ResizeObserver loop limit exceeded",
            "ResizeObserver loop completed with undelivered notifications.",
        ],
        attachStacktrace: true,
    };
};
